import { ethers } from 'ethers';
import type {
  CryptoAssetEstimateGasParams,
  CryptoAssetGasEstimate,
  CryptoAssetSendParams,
  CryptoAssetTransactionResult,
} from './ICryptoAssetProvider';
import type { CryptoAsset } from '@/types/WalletTypes';
import {
  EvmErc20CryptoAssetProvider,
  EvmNativeCryptoAssetProvider,
  getEvmGasQuote,
  getEvmTxnContext,
  resolveNativeSendValue,
  signAndSendNativeTxn,
} from './EvmCryptoAssetProvider';
import { requireMnemonic } from './shared';

const ERC20_ABI = [
  'function balanceOf(address owner) view returns (uint256)',
  'function transfer(address to, uint256 amount) returns (bool)',
];
const GAS_PRICE_ORACLE_ADDRESS = '0x420000000000000000000000000000000000000F';
const GAS_PRICE_ORACLE_ABI = ['function getL1FeeUpperBound(uint256 unsignedTxSize) view returns (uint256)'];
const BASE_CHAIN_IDS = new Set([8453, 84532]);

export class EvmBaseNativeCryptoAssetProvider extends EvmNativeCryptoAssetProvider {
  readonly name = 'evm-base-native';

  supports(cryptoAsset: CryptoAsset): boolean {
    return (
      cryptoAsset.traits.isEvm &&
      BASE_CHAIN_IDS.has(Number(cryptoAsset.asset.blockchainConfiguration.chainId)) &&
      cryptoAsset.traits.isNative
    );
  }

  async estimateGas({ amount, cryptoAsset, from, to }: CryptoAssetEstimateGasParams): Promise<CryptoAssetGasEstimate> {
    const rpcProvider = this.getRpcProvider(cryptoAsset);
    const requestedValue = ethers.utils.parseUnits(amount.toString(), cryptoAsset.display.decimalPrecision);
    const txnRequest = { from, to, value: requestedValue };
    const gasQuote = await getEvmGasQuote({
      estimatedGasLimit: await rpcProvider.estimateGas(txnRequest),
      provider: rpcProvider,
      usesEIP1559: cryptoAsset.asset.blockchainConfiguration.usesEIP1559,
    });
    const l1Fee = await estimateBaseL1Fee(rpcProvider, txnRequest);
    const fee = gasQuote.fee.add(l1Fee);

    return {
      coin: ethers.utils.formatUnits(fee, cryptoAsset.display.decimalPrecision),
      usd: 0,
    };
  }

  async send({
    amount,
    cryptoAsset,
    isMaxSend,
    mnemonic,
    privateKey,
    to,
  }: CryptoAssetSendParams): Promise<CryptoAssetTransactionResult> {
    const rpcProvider = this.getRpcProvider(cryptoAsset);
    const wallet = privateKey
      ? new ethers.Wallet(privateKey).connect(rpcProvider)
      : ethers.Wallet.fromMnemonic(requireMnemonic(mnemonic)).connect(rpcProvider);
    const txnContext = await getEvmTxnContext({
      provider: rpcProvider,
      wallet,
    });
    const requestedValue = ethers.utils.parseUnits(amount.toString(), cryptoAsset.display.decimalPrecision);
    const txnRequest = { from: txnContext.connectedWallet.address, to, value: requestedValue };
    const gasQuote = await getEvmGasQuote({
      estimatedGasLimit: await rpcProvider.estimateGas(txnRequest),
      provider: rpcProvider,
      usesEIP1559: cryptoAsset.asset.blockchainConfiguration.usesEIP1559,
    });
    const l1Fee = await estimateBaseL1Fee(rpcProvider, txnRequest);
    const fee = gasQuote.fee.add(l1Fee);
    const sendValue = await resolveNativeSendValue({
      fee,
      isMaxSend,
      provider: rpcProvider,
      requestedValue,
      walletAddress: txnContext.connectedWallet.address,
    });

    return signAndSendNativeTxn({
      provider: rpcProvider,
      cryptoAsset,
      gasQuote,
      to,
      txnContext,
      sendValue,
    });
  }
}

export class EvmBaseErc20CryptoAssetProvider extends EvmErc20CryptoAssetProvider {
  readonly name = 'evm-base-erc20';

  supports(cryptoAsset: CryptoAsset): boolean {
    return (
      cryptoAsset.traits.isEvm &&
      BASE_CHAIN_IDS.has(Number(cryptoAsset.asset.blockchainConfiguration.chainId)) &&
      cryptoAsset.traits.isErc20
    );
  }

  async estimateGas({ amount, cryptoAsset, from, to }: CryptoAssetEstimateGasParams): Promise<CryptoAssetGasEstimate> {
    const rpcProvider = this.getRpcProvider(cryptoAsset);
    const requestedTokenAmount = ethers.utils.parseUnits(amount.toString(), cryptoAsset.display.decimalPrecision);
    const contract = new ethers.Contract(cryptoAsset.asset.definitionAddress, ERC20_ABI, rpcProvider);
    const data = contract.interface.encodeFunctionData('transfer', [to, requestedTokenAmount]);
    const gasQuote = await getEvmGasQuote({
      estimatedGasLimit: await contract.estimateGas.transfer(to, requestedTokenAmount, { from }),
      provider: rpcProvider,
      usesEIP1559: cryptoAsset.asset.blockchainConfiguration.usesEIP1559,
    });
    const l1Fee = await estimateBaseL1Fee(rpcProvider, {
      data,
      from,
      to: cryptoAsset.asset.definitionAddress,
      value: 0,
    });
    const fee = gasQuote.fee.add(l1Fee);

    return {
      coin: ethers.utils.formatUnits(fee, cryptoAsset.fee.decimalPrecision),
      usd: 0,
    };
  }
}

const estimateBaseL1Fee = async (
  rpcProvider: ethers.providers.Provider,
  txnRequest: ethers.providers.TransactionRequest
) => {
  try {
    const oracle = new ethers.Contract(GAS_PRICE_ORACLE_ADDRESS, GAS_PRICE_ORACLE_ABI, rpcProvider);
    const serializedTxn = ethers.utils.serializeTransaction({
      ...txnRequest,
      gasLimit: 0,
      nonce: 0,
    });
    return oracle.getL1FeeUpperBound(ethers.utils.arrayify(serializedTxn).length);
  } catch {
    return ethers.BigNumber.from(0);
  }
};
